// src/pages/workflow/PipelinePolicyListPage.tsx
// Governance policy admin — CreatePipelinePolicy, GetPipelinePolicy,
// UpdatePipelinePolicy, DeletePipelinePolicy, ListPipelinePolicies. A
// pipeline's Settings tab shows the *resolved* policy read-only
// (GetPipelinePolicyByEntity / ResolvePipelineContext); this page is where an
// admin actually writes the records those resolve against.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useWorkflowPipelinePolicy, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Badge,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { getEntityId, triggerTypeOptions, errorHandlingOptions } from '@/features/workflow/logic';

const SCOPE_TYPES = ['tenant', 'product', 'platform'];

const emptyForm = {
  name: '',
  description: '',
  scope_entity_type: 'tenant',
  default_error_handling: 'ERROR_HANDLING_STOP_ON_ERROR',
  default_timeout_sec: '',
  max_concurrent_executions: '',
  max_retries: '',
  allowed_trigger_types: [] as string[],
  require_publish_approval: false,
  is_active: true,
};

export const PipelinePolicyListPage = observer(() => {
  const store = useWorkflowPipelinePolicy();
  const { t } = useI18n();

  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialog, setDialog] = useState<null | { mode: 'create' | 'edit'; id?: string }>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    store.ListPipelinePolicies?.({ limit: 100 } as any)
      .then(() => setPolicies(store.items || []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setDialog({ mode: 'create' });
  };

  const openEdit = async (policyId: string) => {
    setDialog({ mode: 'edit', id: policyId });
    try {
      const p = await store.GetPipelinePolicy?.(policyId) as any;
      setForm({
        name: p?.name ?? '',
        description: p?.description ?? '',
        scope_entity_type: p?.scope_entity_type ?? 'tenant',
        default_error_handling: p?.default_error_handling ?? 'ERROR_HANDLING_STOP_ON_ERROR',
        default_timeout_sec: p?.default_timeout_sec ? String(p.default_timeout_sec) : '',
        max_concurrent_executions: p?.max_concurrent_executions ? String(p.max_concurrent_executions) : '',
        max_retries: p?.max_retries ? String(p.max_retries) : '',
        allowed_trigger_types: p?.allowed_trigger_types ?? [],
        require_publish_approval: !!p?.require_publish_approval,
        is_active: p?.is_active ?? true,
      });
    } catch {
      // Error handled by store
    }
  };

  const toggleTrigger = (v: string) => {
    setForm((prev) => ({
      ...prev,
      allowed_trigger_types: prev.allowed_trigger_types.includes(v)
        ? prev.allowed_trigger_types.filter((x) => x !== v)
        : [...prev.allowed_trigger_types, v],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setIsSaving(true);
    try {
      const timeout = form.default_timeout_sec ? Number(form.default_timeout_sec) : undefined;
      const maxConcurrent = form.max_concurrent_executions ? Number(form.max_concurrent_executions) : undefined;
      const maxRetries = form.max_retries ? Number(form.max_retries) : undefined;
      if (dialog?.mode === 'edit' && dialog.id) {
        const existing = policies.find((p) => getEntityId(p) === dialog.id) ?? {};
        await store.UpdatePipelinePolicy?.(dialog.id, {
          policy: {
            ...existing,
            ...form,
            name: form.name.trim(),
            default_timeout_sec: timeout,
            max_concurrent_executions: maxConcurrent,
            max_retries: maxRetries,
          } as any,
        });
      } else {
        await store.CreatePipelinePolicy?.({
          name: form.name.trim(),
          description: form.description || undefined,
          scope_entity_type: form.scope_entity_type,
          allowed_node_type_ids: [],
          default_error_handling: form.default_error_handling as any,
          default_timeout_sec: timeout,
          max_concurrent_executions: maxConcurrent,
          max_retries: maxRetries,
          allowed_trigger_types: form.allowed_trigger_types as any,
          require_publish_approval: form.require_publish_approval,
        });
      }
      setDialog(null);
      load();
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (id: string) => {
    await store.DeletePipelinePolicy?.(id);
    setConfirmId(null);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm">
            <Link to="/workflows" className="text-muted-foreground hover:text-foreground">{t('workflow.pipelines')}</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('workflow.policyAdmin.title')}</span>
          </nav>
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('workflow.policyAdmin.title')}</h1>
        </div>
        <Button size="sm" onClick={openCreate}>{t('workflow.policyAdmin.create')}</Button>
      </div>

      {Boolean(store.error) && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}><CardContent className="p-5"><div className="h-16 animate-pulse rounded bg-muted" /></CardContent></Card>
          ))}
        </div>
      ) : policies.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">{t('workflow.policyAdmin.empty')}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {policies.map((p) => {
            const pid = getEntityId(p);
            return (
              <Card key={pid}>
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">{p.name}</h3>
                    <Badge variant={p.is_active ? 'success' : 'secondary'} size="sm">
                      {p.is_active ? t('workflow.active') : t('workflow.policyAdmin.inactive')}
                    </Badge>
                  </div>
                  {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
                  <div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                    <span>{t('workflow.policyAdmin.scope')}: {p.scope_entity_type || '-'}</span>
                    {p.require_publish_approval && <Badge variant="warning" size="sm">{t('workflow.requiresApproval')}</Badge>}
                  </div>
                  <div className="flex justify-end gap-1.5 pt-2 border-t border-border">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(pid)}>{t('common.edit')}</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmId(pid)}>{t('common.delete')}</Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <FormDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onSubmit={save}
        submitLabel={t('common.save')}
        canSubmit={!!form.name.trim()}
        isSubmitting={isSaving}
        dirty={!!form.name.trim() || !!form.description.trim()}
        width="lg"
        aria-label={dialog?.mode === 'edit' ? t('workflow.policyAdmin.edit') : t('workflow.policyAdmin.create')}
      >
        <div className="space-y-4 px-3.5 pb-4 pt-2">
          <TitleInput
            value={form.name}
            onChange={(value) => setForm({ ...form, name: value })}
            placeholder={t('workflow.field.name')}
            initialFocus
            onEnter={() => document.getElementById('pipeline-policy-description')?.focus()}
            aria-label={t('workflow.field.name')}
          />
          <AutoGrowTextarea
            id="pipeline-policy-description"
            value={form.description}
            onChange={(value) => setForm({ ...form, description: value })}
            placeholder={t('workflow.field.description')}
            minRows={1}
          />
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">{t('workflow.policyAdmin.scope')}</label>
            <SegmentedControl
              aria-label={t('workflow.policyAdmin.scope')}
              value={form.scope_entity_type}
              onChange={(value) => setForm({ ...form, scope_entity_type: value })}
              options={SCOPE_TYPES.map((s) => ({ value: s, label: s }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input label={t('workflow.defaultTimeout')} type="number" value={form.default_timeout_sec} onChange={(e) => setForm({ ...form, default_timeout_sec: e.target.value })} />
            <Input label={t('workflow.maxConcurrent')} type="number" value={form.max_concurrent_executions} onChange={(e) => setForm({ ...form, max_concurrent_executions: e.target.value })} />
            <Input label={t('workflow.policyAdmin.maxRetries')} type="number" value={form.max_retries} onChange={(e) => setForm({ ...form, max_retries: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">{t('workflow.allowedTriggers')}</label>
            <div className="flex flex-wrap gap-2">
              {triggerTypeOptions.map((o) => (
                <label key={o.value} className="flex items-center gap-1.5 text-sm text-foreground">
                  <input type="checkbox" checked={form.allowed_trigger_types.includes(o.value)} onChange={() => toggleTrigger(o.value)} className="rounded border-input" />
                  {t(o.labelKey)}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">{t('workflow.field.errorHandling')}</label>
            <SegmentedControl
              aria-label={t('workflow.field.errorHandling')}
              value={form.default_error_handling}
              onChange={(value) => setForm({ ...form, default_error_handling: value })}
              options={errorHandlingOptions.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </div>
          <ToggleSwitch
            label={t('workflow.requiresApproval')}
            checked={form.require_publish_approval}
            onChange={(checked) => setForm({ ...form, require_publish_approval: checked })}
          />
          {dialog?.mode === 'edit' && (
            <ToggleSwitch
              label={t('workflow.active')}
              checked={form.is_active}
              onChange={(checked) => setForm({ ...form, is_active: checked })}
            />
          )}
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmId}
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && remove(confirmId)}
        title={t('workflow.policyAdmin.deleteConfirmTitle')}
        description={t('workflow.policyAdmin.deleteConfirmDesc')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
});

export default PipelinePolicyListPage;
